import axios from "axios";
import request from "../../network/request";

/**
 * 事故维修附件上传服务（方案 1 / UUID）
 *
 * 流程：getUploadUrl（取 OSS 预签名地址，复用现有接口）→ 前端 PUT 直传
 *      → saveFileInfo（downloadUrl 换 fileId）→ 提交存 fileId
 * 回显：listByFileIds（fileId → filePath/thumbUrl/fileType）
 *
 * 后端服务 xp-ass-toc：
 *   POST /public/v1/attachment/saveFileInfo   { url } → { fileId }
 *   POST /public/v1/attachment/listByFileIds  { splicingFileIds } → [{ fileId, filePath, thumbUrl, fileType, ... }]
 */

// 附件数量与单个大小限制
export const ATTACHMENT_MAX = 9;
export const ATTACHMENT_MAX_SIZE = 50 * 1024 * 1024; // 50M
// 图片单独上限 20M：对齐阿里云 OSS 图片处理输入上限（20971520 字节），超过则缩略图生成失败
export const IMAGE_MAX_SIZE = 20 * 1024 * 1024; // 20M

// 允许上传的格式白名单（默认值，组件可通过 props 覆盖）
// 图片：jpg/jpeg/png；视频：mp4/mov（覆盖 iPhone .mov / 安卓 .mp4）
export const ALLOWED_IMAGE_EXT = ["jpg", "jpeg", "png"];
export const ALLOWED_VIDEO_EXT = ["mp4", "mov"];

export type AttachmentType = "image" | "video";

export interface AttachmentItem {
  fileId: string; // saveFileInfo 返回的 UUID（提交时存入 attachmentList）
  url: string; // 原文件地址（图片大图预览 / 视频播放）
  type: AttachmentType;
  thumbUrl?: string; // 缩略图地址（视频为 OSS 截帧图；后端 listByFileIds 返回）
  fileName?: string;
}

function isVideoFile(file: File): boolean {
  if (file.type) return file.type.startsWith("video/");
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  return ALLOWED_VIDEO_EXT.includes(ext);
}

// 根据地址或后端 fileType 判断图/视频
export function resolveType(url = "", fileType = ""): AttachmentType {
  const ext = (fileType || url.split("?")[0].split(".").pop() || "").toLowerCase();
  return ALLOWED_VIDEO_EXT.includes(ext) ? "video" : "image";
}

// ① 取上传地址（复用现有接口）
async function getUploadUrl(
  fileName: string
): Promise<{ uploadUrl: string; downloadUrl: string }> {
  const data: any = await request(
    "serviceOrder/get/upload/url",
    { fileName },
    "POST"
  );
  const uploadUrl: string = data?.uploadUrl;
  const downloadUrl: string = data?.downloadUrl;
  // 接口可能返回 200 但字段缺失，此处显式断言，缺失即抛错走「上传失败，请重试」分支，避免假成功
  if (!uploadUrl || !downloadUrl) {
    throw new Error("获取上传地址失败：uploadUrl/downloadUrl 为空");
  }
  return { uploadUrl, downloadUrl };
}

// ② 登记附件换 UUID（xp-ass-toc）
async function saveFileInfo(url: string): Promise<string> {
  const data: any = await request(
    "attachment/saveFileInfo",
    { url },
    "POST"
  );
  const fileId: string = data?.fileId;
  // 缺失 fileId 时抛错，避免返回空串导致提交空 ID、附件静默丢失
  if (!fileId) {
    throw new Error("附件登记失败：fileId 为空");
  }
  return fileId;
}

// ③ 按 fileId 回显（xp-ass-toc）
export async function listByFileIds(
  fileIds: string[]
): Promise<AttachmentItem[]> {
  if (!fileIds || !fileIds.length) return [];
  const data: any = await request(
    "attachment/listByFileIds",
    {
      splicingFileIds: fileIds.join(","),
      // 缩略图生成参数（后端据此返回 thumbUrl）；240 适配 2x/3x 高清屏，图片加 q_80 省流量
      ossProcess: "image/resize,m_fill,h_240,w_240/quality,q_80",
      ossVideoProcess: "video/snapshot,t_1000,f_jpg,w_240,h_240,m_fast",
    },
    "POST"
  );
  return (data || []).map((it: any) => ({
    fileId: it.fileId,
    url: it.filePath,
    thumbUrl: it.thumbUrl, // 缩略图（视频为 OSS 截帧图）
    type: resolveType(it.filePath, it.fileType),
    fileName: it.fileName,
  }));
}

/**
 * 上传单个文件：取地址 → PUT 直传 → saveFileInfo 换 fileId
 * 再用 listByFileIds 取 presigned filePath / thumbUrl 用于展示预览
 */
export async function uploadFile(file: File): Promise<AttachmentItem> {
  const type: AttachmentType = isVideoFile(file) ? "video" : "image";

  const { uploadUrl, downloadUrl } = await getUploadUrl(file.name);
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": "application/octet-stream" },
  });
  const fileId = await saveFileInfo(downloadUrl);

  // 取可展示的 filePath / thumbUrl（filePath 为 presigned 可访问地址）
  const [info] = await listByFileIds([fileId]);
  return {
    fileId,
    url: info?.url || downloadUrl,
    thumbUrl: info?.thumbUrl,
    type: info?.type || type,
    fileName: file.name,
  };
}

/**
 * 轻量上传：取地址 → PUT 直传 → saveFileInfo 换 fileId，仅返回 fileId。
 * 用于「只需要 fileId、不需要预览/缩略图」的场景（如异响位置图）——
 * 省去 listByFileIds 一趟网络及后端缩略图生成，减少上传耗时。
 */
export async function uploadFileForId(file: File): Promise<string> {
  const { uploadUrl, downloadUrl } = await getUploadUrl(file.name);
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": "application/octet-stream" },
  });
  return saveFileInfo(downloadUrl);
}
